import { useApi, type UseApiResult } from './api/useApi';
import { useMutate, type MutateOptions } from './api/useMutate';
import { queryCache } from './api/queryCache';

export interface QueryOptions<TData> {
  queryKey: unknown[];
  queryFn: () => Promise<TData>;
  enabled?: boolean;
  placeholderData?: TData;
  [key: string]: unknown;
}

export function useQuery<TData>(
  options: QueryOptions<TData>,
): UseApiResult<TData> & { isLoading: boolean; isPending: boolean; isSuccess: boolean } {
  const result = useApi<TData>(options?.queryKey ?? [], options.queryFn, {
    enabled: options?.enabled,
    placeholderData: options?.placeholderData,
  });
  return {
    ...result,
    isLoading: result.loading,
    isPending: result.loading,
    isSuccess: !result.loading && !result.error,
  };
}

export function useSuspenseQuery<TData>(
  options: QueryOptions<TData>,
): Omit<ReturnType<typeof useQuery<TData>>, 'data'> & { data: TData } {
  return useQuery<TData>(options) as Omit<ReturnType<typeof useQuery<TData>>, 'data'> & { data: TData };
}

export function useMutation<TResult, TInput = void>(
  options: { mutationFn: (input: TInput) => Promise<TResult> } & MutateOptions<TResult, TInput>,
) {
  const m = useMutate<TResult, TInput>(options.mutationFn, options);
  return { ...m, isLoading: m.isPending };
}

export const useQueryClient = () => {
  return {
    invalidateQueries: (options: { queryKey?: unknown[] }) => {
      if (options && options.queryKey) {
        queryCache.invalidate(options.queryKey);
      }
    },
    setQueryData: (queryKey: unknown[], data: unknown) => {
      queryCache.set(queryKey, data);
    },
    getQueryData: (queryKey: unknown[]) => {
      return queryCache.get(queryKey);
    },
  };
};

export const QueryClientProvider = ({ children }: { children?: unknown }) => children;
export const QueryClient = class {};
